import React, { useState } from 'react'

/* ─────────────────────────────────────────────────────────────
   Sidebar
   Added:
   - Language switcher (EN / FR / NL) — UI only, no i18n wired yet
   - Request History nav item
   Props:
     activeSection  string
     collapsed      bool
     isMobile       bool
     drawerOpen     bool
     onNavigate     fn(id)
     onCollapse     fn()
     onSignOut      fn()
     member         { name, initials, memberId }
     lang           string ('en' | 'fr' | 'nl')
     onLangChange   fn(lang)
───────────────────────────────────────────────────────────── */

const NAV_ITEMS = [
  { id: 'aviation',  label: 'Flight Arrangements' },
  { id: 'hotels',    label: 'Partner Hotels'   },
  { id: 'transfers', label: 'Car Transfers'    },
  { id: 'airport',   label: 'Airport Services' },
  { id: 'about',     label: 'About'            },
  { id: 'blog',      label: 'Blog'             },
]

export { NAV_ITEMS }

const LANGUAGES = [
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
  { code: 'nl', label: 'NL' },
]

export default function Sidebar({
  activeSection,
  collapsed,
  isMobile,
  drawerOpen,
  onNavigate,
  onCollapse,
  onSignOut,
  member,
  lang = 'en',
  onLangChange,
}) {
  const [langOpen, setLangOpen] = useState(false)

  const sidebarClass = [
    'db-sidebar',
    collapsed && !isMobile ? 'db-sidebar--slim' : '',
    isMobile               ? 'db-sidebar--mobile' : '',
    isMobile && drawerOpen ? 'db-sidebar--open' : '',
  ].filter(Boolean).join(' ')

  const currentLang = LANGUAGES.find(l => l.code === lang) || LANGUAGES[0]

  const selectLang = (code) => {
    onLangChange?.(code)
    setLangOpen(false)
  }

  return (
    <aside className={sidebarClass} aria-label="Main navigation">

      {/* ── Brand ── */}
      <div className="db-sidebar__brand">
        <div className="db-sidebar__brand-text">
          <img src="/images/logo.png" alt="Mwuïlu" />
        </div>
        {!isMobile && (
          <button
            className="db-sidebar__collapser"
            onClick={onCollapse}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <CollapseIcon collapsed={collapsed} />
          </button>
        )}
      </div>

      {/* ── Navigation ── */}
      <nav className="db-nav">
        <ul className="db-nav__list" role="list">

          {/* Home */}
          <li>
            <button
              className={`db-nav__btn${activeSection === 'home' ? ' db-nav__btn--active' : ''}`}
              onClick={() => onNavigate('home')}
              title={collapsed ? 'Home' : undefined}
            >
              <span className="db-nav__icon"><IconHome /></span>
              {!collapsed && <span className="db-nav__label">Home</span>}
            </button>
          </li>

          {/* Request History */}
          <li>
            <button
              className={`db-nav__btn${activeSection === 'history' ? ' db-nav__btn--active' : ''}`}
              onClick={() => onNavigate('history')}
              title={collapsed ? 'Request History' : undefined}
            >
              <span className="db-nav__icon"><IconHistory /></span>
              {!collapsed && <span className="db-nav__label">Request History</span>}
            </button>
          </li>

          {!collapsed && <li className="db-nav__divider" aria-hidden="true" />}

          {NAV_ITEMS.map(item => (
            <li key={item.id}>
              <button
                className={`db-nav__btn${activeSection === item.id ? ' db-nav__btn--active' : ''}`}
                onClick={() => onNavigate(item.id)}
                title={collapsed ? item.label : undefined}
              >
                <span className="db-nav__icon"><NavIcon id={item.id} /></span>
                {!collapsed && <span className="db-nav__label">{item.label}</span>}
              </button>
            </li>
          ))}

        </ul>
      </nav>

      {/* ── Sidebar footer ── */}
      <div className="db-sidebar__foot">

        {/* Language switcher */}
        {!collapsed ? (
          <div className="db-lang-switcher">
            {LANGUAGES.map(l => (
              <button
                key={l.code}
                className={`db-lang-btn${lang === l.code ? ' db-lang-btn--active' : ''}`}
                onClick={() => selectLang(l.code)}
                aria-label={`Switch to ${l.label}`}
                aria-pressed={lang === l.code}
              >
                {l.label}
              </button>
            ))}
          </div>
        ) : (
          /* Collapsed: just show current language code as a small button */
          <div className="db-lang-switcher db-lang-switcher--collapsed">
            <button
              className="db-lang-btn db-lang-btn--active"
              onClick={() => setLangOpen(o => !o)}
              aria-label="Switch language"
              title="Switch language"
            >
              {currentLang.label}
            </button>
            {langOpen && (
              <div className="db-lang-dropdown">
                {LANGUAGES.filter(l => l.code !== lang).map(l => (
                  <button
                    key={l.code}
                    className="db-lang-dropdown__item"
                    onClick={() => selectLang(l.code)}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Member chip → profile */}
        <button
          className={`db-member db-member--btn${activeSection === 'profile' ? ' db-member--active' : ''}`}
          onClick={() => onNavigate('profile')}
          title={collapsed ? 'My Profile' : undefined}
          aria-label="View my profile"
        >
          <div className="db-member__avatar">{member.initials}</div>
          {!collapsed && (
            <div className="db-member__text">
              <span className="db-member__name">{member.name}</span>
              <span className="db-member__id">{member.memberId}</span>
            </div>
          )}
        </button>

        {/* Sign out */}
        <button
          className="db-signout"
          onClick={onSignOut}
          aria-label="Sign out"
          title="Sign out"
        >
          <IconSignOut />
          {!collapsed && <span>Sign Out</span>}
        </button>

      </div>

    </aside>
  )
}

/* ── Icons ── */
const SV = { fill: 'none', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round' }

function IconHome() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M2 7L8 2L14 7V14H10V10H6V14H2V7Z" {...SV} strokeWidth="0.9" />
    </svg>
  )
}

function IconHistory() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
      <circle cx="7.5" cy="7.5" r="5.5" stroke="currentColor" strokeWidth="0.85" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <path d="M7.5 4.5V7.5L9.5 9.5" stroke="currentColor" strokeWidth="0.85" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function IconSignOut() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M5 2H2a1 1 0 00-1 1v8a1 1 0 001 1h3M10 10l3-3m0 0l-3-3m3 3H5" {...SV} strokeWidth="1" />
    </svg>
  )
}

function CollapseIcon({ collapsed }) {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path d={collapsed ? 'M4 2l4 4-4 4' : 'M8 2L4 6l4 4'} {...SV} strokeWidth="1" />
    </svg>
  )
}

export function NavIcon({ id }) {
  const p = { width: '15', height: '15', viewBox: '0 0 15 15', fill: 'none' }
  const s = { ...SV, strokeWidth: '0.85' }
  switch (id) {
    case 'aviation':
      return <svg {...p}><path d="M13 4L9 7.5H13L10.5 12.5H8.5L7 9L2 11.5V9.5L6 7L2 3V1L7 4L8.5 0H10.5L13 4Z" {...s} /></svg>
    case 'hotels':
      return <svg {...p}><rect x="2" y="6" width="11" height="8" rx="0.5" {...s} /><path d="M4 14V11H6V14M8.5 11H11V13H8.5V11Z" {...s} /><path d="M2 6L7.5 2L13 6" {...s} /></svg>
    case 'transfers':
      return <svg {...p}><rect x="1" y="6" width="13" height="5" rx="1" {...s} /><path d="M3 6L4.5 3H10.5L12 6" {...s} /><circle cx="4" cy="12.5" r="1.5" {...s} /><circle cx="11" cy="12.5" r="1.5" {...s} /></svg>
    case 'airport':
      return <svg {...p}><path d="M2 13H13M7.5 2.5V9.5M5 7L7.5 9.5L10 7" {...s} /></svg>
    case 'about':
      return <svg {...p}><circle cx="7.5" cy="7.5" r="5.5" {...s} /><path d="M7.5 6.5V10.5M7.5 5V5.5" {...{ ...s, strokeWidth: '1' }} /></svg>
    case 'blog':
      return <svg {...p}><rect x="2" y="2" width="11" height="11" rx="1" {...s} /><path d="M4.5 5H10.5M4.5 7.5H8.5M4.5 10H7" {...s} /></svg>
    default:
      return null
  }
}